import React from 'react'

import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import Flex from 'styled-flex-component'

import { IModalComponentProps } from '@context/Modal'
import { useIsMounted } from '@hooks/useIsMounted'
import { IApplicationState } from '@store'
import { deleteBrandFanGroup, selectIsUpdatingBrandFanGroup } from '@store/brandFans'
import Theme from '@theme'
import { Button, Container, H1, Header, Modal, Text } from '@tribegroup'

interface IProps extends IModalComponentProps {
  groupId: number
}

const BrandFansGroupModalDelete: React.FC<IProps> = ({
  groupId,
  isOpen = false,
  onRequestClose,
}) => {
  const dispatch = useDispatch()
  const isUpdatingBrandFanGroup = useSelector((state: IApplicationState) =>
    selectIsUpdatingBrandFanGroup(state),
  )
  const [hasStartedDelete, setHasStartedDelete] = React.useState(false)

  const handleDelete = () => {
    setHasStartedDelete(true)
    dispatch(deleteBrandFanGroup(groupId))
  }

  const isMounted = useIsMounted()
  React.useEffect(() => {
    // Only close the modal after delete has been initiated and completed
    if (isMounted && hasStartedDelete && !isUpdatingBrandFanGroup) {
      onRequestClose()
    }
  }, [isUpdatingBrandFanGroup, isMounted, onRequestClose, hasStartedDelete])

  return (
    <Modal reactModalProps={{ isOpen }} width={33.75} complex>
      <H1 color={Theme.grey900Color} bottomOuterSpacing={1.75} capitalize>
        <FormattedMessage id="brandFans.groups.actions.deleteGroup.modal.title" />
      </H1>
      <Container bottomOuterSpacing={2.5}>
        <Text color={Theme.grey800Color} customFontSize={1.125}>
          <FormattedMessage id="brandFans.groups.actions.deleteGroup.modal.message" />
        </Text>
      </Container>
      <Flex justifyEnd alignCenter>
        <Button
          width={8.75}
          scheme="primary"
          height={3}
          outlined
          onClick={onRequestClose}
          disabled={isUpdatingBrandFanGroup}
          type="button"
          data-testid="brand-fans-group-delete-cancel"
        >
          <Header cta>
            <FormattedMessage id="brandFans.groups.actions.deleteGroup.modal.cancel" />
          </Header>
        </Button>
        <Button
          width={8.75}
          scheme="primary"
          height={3}
          leftOuterSpacing={0.75}
          onClick={handleDelete}
          disabled={isUpdatingBrandFanGroup}
          loading={isUpdatingBrandFanGroup}
          data-testid="brand-fans-group-delete-confirm"
        >
          <Header cta>
            <FormattedMessage id="brandFans.groups.actions.deleteGroup.modal.confirm" />
          </Header>
        </Button>
      </Flex>
    </Modal>
  )
}

export { BrandFansGroupModalDelete }
export default BrandFansGroupModalDelete
